import { useEffect, useRef, useState } from "react";
import { C } from "../tokens";

interface Recognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type SpeechWindow = Window & {
  SpeechRecognition?: new () => Recognition;
  webkitSpeechRecognition?: new () => Recognition;
};

/** Browser-managed dictation only starts on an explicit microphone click.
 * Final phrases append to the current draft; speech never sends a message. */
export function DictationButton({ onTranscript, disabled = false }: {
  onTranscript: (text: string) => void;
  disabled?: boolean;
}) {
  const [listening, setListening] = useState(false);
  const [message, setMessage] = useState("");
  const active = useRef<Recognition | null>(null);
  const callback = useRef(onTranscript);
  callback.current = onTranscript;
  const cancel = () => {
    const recognition = active.current;
    active.current = null;
    if (recognition) {
      recognition.onresult = recognition.onerror = recognition.onend = null;
      recognition.abort();
    }
  };
  useEffect(() => cancel, []);
  useEffect(() => {
    if (disabled) { cancel(); setListening(false); }
  }, [disabled]);

  const toggle = () => {
    if (active.current) { active.current.stop(); return; }
    const browser = window as SpeechWindow;
    const Constructor = browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
    if (!Constructor) {
      setMessage("Dictation is unavailable in this browser. You can still type your message.");
      return;
    }
    setMessage("");
    try {
      const recognition = new Constructor();
      active.current = recognition;
      recognition.lang = navigator.language || "en-US";
      recognition.continuous = true;
      recognition.interimResults = false;
      const delivered = new Set<number>();
      recognition.onresult = (event) => {
        if (active.current !== recognition) return;
        for (let index = event.resultIndex; index < event.results.length; index++) {
          const result = event.results[index]!;
          if (!result.isFinal || delivered.has(index)) continue;
          delivered.add(index);
          const text = result[0].transcript.trim();
          if (text) callback.current(text);
        }
      };
      recognition.onerror = ({ error }) => {
        setMessage(error === "not-allowed" || error === "service-not-allowed"
          ? "Microphone access was denied. Allow it in your browser settings to dictate."
          : error === "no-speech" ? "No speech detected. Try again."
          : "Dictation stopped. Check your microphone and connection, then try again.");
        cancel();
        setListening(false);
      };
      recognition.onend = () => {
        active.current = null;
        setListening(false);
      };
      recognition.start();
      setListening(true);
    } catch {
      cancel();
      setListening(false);
      setMessage("Could not start dictation. Check your microphone permissions and try again.");
    }
  };
  return <div style={{ position: "relative", flexShrink: 0 }}>
    <button type="button" className="kb" disabled={disabled} onClick={toggle}
      aria-label={listening ? "Stop dictation" : "Start dictation"} aria-pressed={listening}
      title={listening ? "Stop dictation" : "Dictate using your browser’s speech service"}
      style={{ width: 36, height: 36, border: 0, borderRadius: 999,
        background: listening ? C.redSoft : "transparent", color: listening ? C.red : C.sub,
        cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1 }}>
      {listening ? <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /></svg>
        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8" />
        </svg>}
    </button>
    <span role="status" style={{ position: "absolute", bottom: "calc(100% + 16px)", right: 0,
      width: 240, fontSize: 13.8, textAlign: "left", color: C.ink,
      ...(message || listening ? { padding: 10, borderRadius: 10, background: C.surface, border: `1px solid ${C.line}` } : {}) }}>
      {message || (listening ? "Listening… Click stop when finished." : "")}
    </span>
  </div>;
}
